import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import checkCircle from 'src/assets/icons/filled-icons/check-circle.svg';
import { selectCompletedSteps, selectCurrentStep, setCurrentStep } from 'src/redux/bookingStepper.slice';

import { getFilteredBookingSteps, IBookingStep, IBookingStopSummary } from './bookingQuote.const';

interface ISidebarProps {
    onStepNavigate?: (stepId: number) => void;
    isCrossBorder?: boolean;
    // Must match the same array passed to the parent's own getFilteredBookingSteps call — this
    // component computes its own step list independently (not passed the parent's as a prop), so
    // the two calls only stay in sync because getFilteredBookingSteps is a pure function of these
    // same inputs. Sorted ascending by position, and carrying city/state for the tab label.
    stops?: IBookingStopSummary[];
}

const Sidebar: FC<ISidebarProps> = ({ onStepNavigate, isCrossBorder = false, stops = [] }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const currentStep = useSelector(selectCurrentStep);
    const completedSteps = useSelector(selectCompletedSteps);

    const steps = useMemo(() => getFilteredBookingSteps(t, isCrossBorder, stops), [t, isCrossBorder, stops]);

    const handleStepClick = (step: IBookingStep) => () => {
        if (onStepNavigate) {
            onStepNavigate(step.id);
        } else {
            dispatch(setCurrentStep(step.id));
        }
    };

    return (
        <ul className="stepper-sidebar-wrap space-y-1">
            {steps.map((step) => {
                const isActive = currentStep === step.id;
                const isCompleted = completedSteps.includes(step.id);
                const Icon = step.icon;

                return (
                    // Bug fix (this feature) — step.key alone isn't unique: every "Stop N" step
                    // shares the literal key 'stop'. step.id is unique per step (reassigned
                    // sequentially by getFilteredBookingSteps), so use that instead.
                    <li key={step.id} className={`vertical-secondary-tab-item ${isActive ? 'vertical-secondary-tab-item-active' : 'cursor-pointer'}`} onClick={handleStepClick(step)}>
                        <div className="vertical-secondary-tab-item-icon-wrap">
                            <Icon className="vertical-secondary-tab-item-icon" />
                        </div>

                        <h3 className="vertical-secondary-tab-item-title">{step.title}</h3>

                        {isCompleted && (
                            <div>
                                <img src={checkCircle} className="vertical-secondary-tab-item-status-icon" alt="check" />
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default Sidebar;
